<script setup lang="ts">
import { ref, computed } from 'vue';
import { useStore } from '@/store';
import * as pako from 'pako';
import axios from 'axios';

const store = useStore();
interface ExtendedFile extends File {
  status?: string;
  errors?: string[];
}

const files = ref<ExtendedFile[]>([]);
const dragActive = ref(false);
const isUploading = ref(false);
const uploadProgress = ref(0);
const logs = ref<string[]>([]);

const progressBarWidth = computed(() => `${uploadProgress.value}%`);

const handleDrag = (e: DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
  if (e.type === "dragenter" || e.type === "dragover") {
    dragActive.value = true;
  } else if (e.type === "dragleave") {
    dragActive.value = false;
  }
};

const handleDrop = (e: DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
  dragActive.value = false;
  if (e.dataTransfer?.files) {
    files.value = Array.from(e.dataTransfer.files);
  }
};

const handleChange = (e: Event) => {
  const target = e.target as HTMLInputElement;
  if (target.files) {
    files.value = [...files.value, ...Array.from(target.files)];
  }
};

// const simulateBackendProcess = async () => {
//   const events = [
//     { message: "Upload complete", delay: 1000, progress: 10 },
//     { message: "Validating CSV format...", delay: 1500, progress: 20 },
//     { message: "CSV format valid", delay: 1000, progress: 30 },
//     { message: "Processing minutes...", delay: 2000, progress: 40 },
//     { message: "Extracting data from minutes...", delay: 2500, progress: 60 },
//     { message: "Preparing database entries...", delay: 1500, progress: 70 },
//     { message: "Writing to database...", delay: 2000, progress: 80 },
//     { message: "Database updated successfully", delay: 1000, progress: 90 },
//     { message: "Generating response...", delay: 1500, progress: 95 },
//     { message: "Process complete", delay: 1000, progress: 100 },
//   ];

//   for (const event of events) {
//     logs.value.push(event.message);
//     uploadProgress.value = event.progress;
//     await new Promise(resolve => setTimeout(resolve, event.delay));
//   }
// };

const handleSubmit = async () => {
  if (files.value.length > 0) {
    isUploading.value = true;
    uploadProgress.value = 0;
    logs.value = [];
    logs.value.push("Starting CSV process...");

    //Compressing
    for (let i = 0; i < files.value.length; i++) {
      const file = files.value[i];
      logs.value.push(`Processing file ${i + 1} of ${files.value.length}: ${file.name}`);
      const reader = new FileReader();
      reader.onload = event => {
        logs.value.push(`Opening CSV...`);
        uploadProgress.value = 0;
        const contents = event?.target?.result;
        const swishName = file.name.split('.')[0]
        logs.value.push(`Compressing CSV...`);
        const output = pako.gzip(contents as string);
        let newFile = new File([output], file.name + ".gz", {
          type: "application/gzip"
        }) as ExtendedFile;
        let formData = new FormData();
        formData.append("swish", newFile);
        logs.value.push("Uploading file: " + file.name);
        axios
          .post(`/api/admin/swishes/${swishName}/data`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
              "Authorization": `Bearer ${store.jwtToken}` 
            },
            onUploadProgress: progressEvent => {
              console.log("progressEvent", progressEvent)
              if (progressEvent.total) {
                const percentCompleted = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total
                );
                logs.value.push(`Upload ${percentCompleted} Complete!`);
                uploadProgress.value = percentCompleted;
                file.status = `Uploading... ${percentCompleted}%`;
              }
            }
          })
          .then(async function () {
            file.status = "Success!";
            logs.value.push(`Server finishing processing!`);
            uploadProgress.value += 14;
            await store.fetchSwishes();
            isUploading.value = false;
            return Promise.resolve();
          })
          .catch(e => {
            try {
              let errors = e.response;
              file.status = `${e.response.data.errors.length} Errors`;
              file.errors = errors;
              errors.forEach((e: string, i: number) => {
                console.error(e);
              });
              return Promise.reject();
            } catch (e) {
              console.error(e);
              file.status = "unexpected errors";
              return Promise.reject();
            }
          });
      };
      reader.onerror = function (event) {
        console.error(
          "File could not be read! Code " + (event.target?.error?.code || 'unknown')
        );
      };
      reader.readAsArrayBuffer(file);
    }
  }
};
</script>

<template>
  <div class="mt-8">
    <h2 class="text-2xl font-semibold mb-4">Upload CSV</h2>
    <div @dragenter="handleDrag" @dragleave="handleDrag" @dragover="handleDrag" @drop="handleDrop"
      class="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer transition-colors"
      :class="{ 'border-blue-500 bg-blue-50': dragActive }">
      <input type="file" id="input-file-upload" accept=".csv" @change="handleChange" class="hidden" multiple />
      <label for="input-file-upload" class="cursor-pointer">
        <div v-if="files.length > 0">
          Selected files: {{ files.map(f => f.name).join(', ') }}
        </div>
        <div v-else>
          <p>Drag and drop your CSV files here, or click to select files</p>
          <p class="text-sm text-gray-500 mt-2">Multiple .csv files are accepted</p>
        </div>
      </label>
    </div>
    <button @click="handleSubmit" :disabled="files.length === 0 || isUploading"
      class="mt-4 bg-blue-500 text-white px-4 py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed">
      {{ isUploading ? 'Uploading...' : 'Upload' }}
    </button>

    <div v-if="isUploading" class="mt-4">
      <div class="w-full bg-gray-800 bg-opacity-30 rounded-full h-2.5 backdrop-blur-sm">
        <div class="bg-blue-500 h-2.5 rounded-full transition-all duration-300 ease-in-out"
          :style="{ width: progressBarWidth }"></div>
      </div>
      <p class="text-sm mt-2 text-blue-300">{{ uploadProgress }}% Complete</p>
    </div>

    <div class="mt-4">
      <h3 class="text-xl font-semibold mb-2">Logs</h3>
      <ul class="bg-gray-800 p-4 rounded-lg max-h-60 overflow-y-auto">
        <li v-for="(log, index) in logs" :key="index" class="text-sm text-gray-300 mb-1">
          {{ log }}
        </li>
      </ul>
    </div>
  </div>
</template>
